import type { PortalConfig } from '@/api/vendo';

// Catalog of "vibe" presets for the customizable portal theme. Picking a vibe
// seeds the background color + suggested effects; the operator can override
// anything afterwards. Keys here MUST match the portal-side vibe renderer
// (device/portal src/styles/vibes.ts) so the live captive portal paints the
// same palette the operator previewed.
export interface VibeMeta {
   key: string;
   name: string;
   emoji: string;
   /** Default background color seeded when this vibe is picked + the card swatch. */
   bg: string;
   /** Accent used for the card swatch dot. */
   accent: string;
   /** Effects suggested (pre-checked) when this vibe is picked. */
   effects: string[];
}

export const PORTAL_VIBES: VibeMeta[] = [
   { key: 'hotel',        name: 'Hotel / Resort',   emoji: '🏨', bg: '#10243b', accent: '#c8a24a', effects: [] },
   { key: 'restaurant',   name: 'Restaurant / Café', emoji: '🍽️', bg: '#2a1810', accent: '#e0853b', effects: [] },
   { key: 'anime',        name: 'Anime',            emoji: '🌸', bg: '#1a1030', accent: '#ff5fa2', effects: ['sakura'] },
   { key: 'gaming',       name: 'Gaming Zone',      emoji: '🎮', bg: '#0c0c1d', accent: '#6c5ce7', effects: ['energy'] },
   { key: 'cyber',        name: 'Cyber City',       emoji: '🌐', bg: '#04111a', accent: '#00e5ff', effects: ['matrix'] },
   { key: 'pinkgirly',    name: 'Pink Girly',       emoji: '🎀', bg: '#2a0a1e', accent: '#ff7eb6', effects: ['sparkles'] },
   { key: 'goldpremium',  name: 'Gold Premium',     emoji: '👑', bg: '#0d0d0d', accent: '#d4af37', effects: ['aura'] },
   { key: 'blackminimal', name: 'Black Minimalist', emoji: '⬛', bg: '#0a0a0a', accent: '#e5e5e5', effects: [] },
   { key: 'christmas',    name: 'Christmas',        emoji: '🎄', bg: '#0a2615', accent: '#e63946', effects: ['snow'] },
   { key: 'beach',        name: 'Beach / Ocean',    emoji: '🏖️', bg: '#053b4a', accent: '#21d4c4', effects: ['bubbles'] },
   { key: 'arcade',       name: 'Neon Arcade',      emoji: '👾', bg: '#140026', accent: '#ff2e88', effects: ['sparkles'] },
   { key: 'forest',       name: 'Forest / Nature',  emoji: '🌲', bg: '#0c1f12', accent: '#4caf50', effects: ['fireflies'] },
   { key: 'scifi',        name: 'Sci-Fi / Space',   emoji: '🚀', bg: '#050816', accent: '#6ab7ff', effects: ['energy'] },
   { key: 'newyear',      name: 'New Year',         emoji: '🎆', bg: '#0a0820', accent: '#ffd54a', effects: ['confetti'] },
   { key: 'valentine',    name: 'Valentine',        emoji: '💖', bg: '#2a0815', accent: '#ff5d8f', effects: ['sakura'] },
   { key: 'halloween',    name: 'Halloween',        emoji: '🎃', bg: '#1a0e02', accent: '#ff7518', effects: ['embers'] },
   { key: 'kids',         name: 'Kids / Cartoon',   emoji: '🧸', bg: '#142a4a', accent: '#ffcc33', effects: ['bubbles'] },
];

// Layout presets — each is a genuinely different page STRUCTURE on the
// captive portal (frames, header, button arrangement), not a recolor. Keys
// MUST match the portal-side renderer (device/portal). Rolled out one at a
// time; add the next entry here + its chrome on the portal when it ships.
export interface LayoutMeta {
   key: string;
   name: string;
   description: string;
   /** Swatch colors for the mini preview card. */
   swatch: { bg: string; bar: string; accent: string };
}

// NOTE: the standard SmartFi look is deliberately NOT offered here — it's
// the platform signature and stays unmodifiable (preset themes only).
export const PORTAL_LAYOUTS: LayoutMeta[] = [
   {
      key: 'classic',
      name: 'Classic Piso',
      description:
         'The familiar piso-wifi look — white page, full-width banner, flat green/red/blue buttons.',
      swatch: { bg: '#ffffff', bar: '#1e88e5', accent: '#43a047' },
   },
   {
      key: 'gaming',
      name: 'Gaming Neon',
      description:
         'Dark arcade look — glowing neon text, animated aura, circular Insert Coin and hexagon buttons.',
      swatch: { bg: '#070b14', bar: '#00e5ff', accent: '#ff2e88' },
   },
   {
      key: 'beach',
      name: 'Beach Resort',
      description:
         'Bright summer blue — frosted white cards over your beach photo, playful rounded buttons with buko, shell & wave icons.',
      swatch: { bg: '#9edcf3', bar: '#0e9bd8', accent: '#ff7a59' },
   },
   {
      key: 'hotel',
      name: 'Hotel & Restaurant',
      description:
         'Premium gold & warm white — your background photo fills the screen, compact status on top, elegant action bar fixed at the bottom.',
      swatch: { bg: '#14181d', bar: '#c9a45c', accent: '#f5eee0' },
   },
];

export interface EffectMeta {
   key: string;
   name: string;
   emoji: string;
}

export const PORTAL_EFFECTS: EffectMeta[] = [
   { key: 'sakura',    name: 'Sakura petals', emoji: '🌸' },
   { key: 'snow',      name: 'Snow',          emoji: '❄️' },
   { key: 'embers',    name: 'Embers / Fire', emoji: '🔥' },
   { key: 'lightning', name: 'Lightning',     emoji: '⚡' },
   { key: 'aura',      name: 'Aura glow',     emoji: '✨' },
   { key: 'energy',    name: 'Power / Energy', emoji: '🌀' },
   { key: 'sparkles',  name: 'Sparkles',      emoji: '⭐' },
   { key: 'bubbles',   name: 'Bubbles',       emoji: '🫧' },
   { key: 'confetti',  name: 'Confetti',      emoji: '🎉' },
   { key: 'coins',     name: 'Floating coins', emoji: '🪙' },
   { key: 'rain',      name: 'Rain',          emoji: '🌧️' },
   { key: 'fireflies', name: 'Fireflies',     emoji: '🟡' },
   { key: 'matrix',    name: 'Matrix rain',   emoji: '💻' },
   { key: 'shells',    name: 'Seashells',     emoji: '🐚' },
   { key: 'palmleaves', name: 'Palm leaves',  emoji: '🍃' },
   { key: 'seagulls',  name: 'Seagulls',      emoji: '🕊️' },
   { key: 'fish',      name: 'Fish',          emoji: '🐠' },
];

// A blank config — the starting point before any vibe is picked. The neutral
// professional slate matches the portal's 'custom' base preset.
export function defaultPortalConfig(): PortalConfig {
   return {
      layout: 'classic',
      buttons: {
         insertCoin: { label: '', color: '', icon: '', shape: '', style: '' },
         wifiRates: { label: '', color: '', icon: '', shape: '', style: '' },
         voucher: { label: '', color: '', icon: '', shape: '', style: '' },
      },
      vibe: '',
      accent: '',
      background: {
         mode: 'color',
         color: '#0e1116',
         gradient: '',
         imageUrl: '',
         videoUrl: '',
         opacity: 1,
      },
      slider: {
         enabled: false,
         images: [],
         height: 200,
      },
      audio: {
         countdownMp3: '',
         insertCoinMp3: '',
      },
      effects: [],
      brand: { companyName: '' },
   };
}

// Merge a possibly-partial stored config (older rows, missing nested keys)
// with defaults so the drawer never reads undefined.
export function normalizePortalConfig(
   stored?: PortalConfig | null
): PortalConfig {
   const d = defaultPortalConfig();
   if (!stored) return d;
   return {
      // Old saves ('' = the retired SmartFi layout choice) roll to classic.
      layout: stored.layout || d.layout,
      buttons: {
         insertCoin: { ...d.buttons!.insertCoin, ...(stored.buttons?.insertCoin || {}) },
         wifiRates: { ...d.buttons!.wifiRates, ...(stored.buttons?.wifiRates || {}) },
         voucher: { ...d.buttons!.voucher, ...(stored.buttons?.voucher || {}) },
      },
      vibe: stored.vibe ?? d.vibe,
      accent: stored.accent ?? d.accent,
      background: { ...d.background, ...(stored.background || {}) },
      slider: { ...d.slider, ...(stored.slider || {}) },
      audio: { ...d.audio, ...(stored.audio || {}) },
      effects: Array.isArray(stored.effects) ? stored.effects : d.effects,
      layoutConfigs: stored.layoutConfigs || {},
      brand: { companyName: stored.brand?.companyName ?? '' },
   };
}


/* ── Layout pick ─────────────────────────────────────────────────────
   Picking a layout swaps the portal page structure. An admin-curated
   default for that layout wins when one exists — the operator starts
   from the curated look and tweaks from there. Otherwise each layout
   brings its own signature colors on the portal side, so only reset the
   background color when the operator hasn't customized it (still a known
   layout default) — never clobber an uploaded image or their
   slider/audio/effects. */
export const LAYOUT_DEFAULT_BG: Record<string, string> = {
   '': '#0e1116',
   classic: '#ffffff',
   gaming: '#070b14',
   hotel: '#14181d',
   beach: '#bfe7f6',
};

/** The look a layout has when the operator has NOT touched it: the clean
 *  layout base, overlaid with the admin-curated default when one exists.
 *  Only the five per-layout fields — buttons are global. */
function untouchedLayoutLook(
   layout: string,
   curated?: Partial<PortalConfig>
): LayoutLook {
   const d = defaultPortalConfig();
   const base: LayoutLook = {
      accent: '',
      background: {
         ...d.background,
         color: LAYOUT_DEFAULT_BG[layout] ?? d.background.color,
      },
      slider: { ...d.slider },
      audio: { ...d.audio },
      effects: [],
   };
   if (!curated) return base;
   return {
      accent: curated.accent ?? '',
      background: { ...base.background, ...(curated.background || {}) },
      slider: { ...base.slider, ...(curated.slider || {}) },
      audio: { ...base.audio, ...(curated.audio || {}) },
      effects: Array.isArray(curated.effects) ? [...curated.effects] : [],
   };
}

type LayoutLook = Pick<
   PortalConfig,
   'accent' | 'background' | 'slider' | 'audio' | 'effects'
>;

const sameLook = (a: LayoutLook, b: LayoutLook) =>
   JSON.stringify([a.accent, a.background, a.slider, a.audio, a.effects]) ===
   JSON.stringify([b.accent, b.background, b.slider, b.audio, b.effects]);

/** @param curated         admin default for the layout being PICKED
 *  @param leavingCurated  admin default for the layout being LEFT — used to
 *                         tell an untouched layout from a customized one */
export function applyLayoutPick(
   value: PortalConfig,
   layout: string,
   curated?: Partial<PortalConfig>,
   leavingCurated?: Partial<PortalConfig>
): PortalConfig {
   // Every layout keeps its OWN config (Ray 2026-07-07). Switching:
   // 1. stash the current layout's accent/background/slider/audio/effects
   //    into layoutConfigs,
   // 2. load the picked layout's stash if it has one (his own settings,
   //    permanent once made),
   // 3. first-time pick only → admin-curated default, else clean layout
   //    defaults. Nothing ever carries over between layouts. Button
   //    styles stay global: each layout reads only the fields it uses.
   const d = defaultPortalConfig();
   const leaving = value.layout || 'classic';
   const current: LayoutLook = {
      accent: value.accent ?? '',
      background: { ...value.background },
      slider: { ...value.slider },
      audio: { ...value.audio },
      effects: [...value.effects],
   };
   const stash: Record<string, unknown> = { ...(value.layoutConfigs || {}) };
   // Remember the layout being left ONLY if the operator changed it. An
   // untouched layout is forgotten, so picking it again always applies the
   // admin default as it is TODAY — not the copy from the day it was first
   // picked (Ray 2026-09-14: "they didn't customize it, it should apply the
   // default from admin").
   if (sameLook(current, untouchedLayoutLook(leaving, leavingCurated))) {
      delete stash[leaving];
   } else {
      stash[leaving] = current;
   }
   const own = stash[layout] as Partial<PortalConfig> | undefined;
   const base: PortalConfig = {
      ...value,
      layout,
      accent: '',
      background: {
         ...d.background,
         color: LAYOUT_DEFAULT_BG[layout] ?? d.background.color,
      },
      audio: { ...d.audio },
      effects: [],
      slider: { ...d.slider },
      layoutConfigs: stash as PortalConfig['layoutConfigs'],
   };
   const seed = own ?? curated;
   if (!seed) return base;
   return {
      ...base,
      accent: seed.accent ?? '',
      background: { ...base.background, ...(seed.background || {}) },
      audio: { ...base.audio, ...(seed.audio || {}) },
      effects: Array.isArray(seed.effects) ? [...seed.effects] : [],
      slider: { ...base.slider, ...(seed.slider || {}) },
      // Curated defaults may carry button styling (gaming orb decor);
      // per-button merge so the device's own tweaks on other fields stay.
      buttons: seed.buttons
         ? {
              insertCoin: {
                 ...(value.buttons?.insertCoin || {}),
                 ...((seed.buttons as any).insertCoin || {}),
              },
              wifiRates: {
                 ...(value.buttons?.wifiRates || {}),
                 ...((seed.buttons as any).wifiRates || {}),
              },
              voucher: {
                 ...(value.buttons?.voucher || {}),
                 ...((seed.buttons as any).voucher || {}),
              },
           }
         : value.buttons,
   };
}

/** "Reset default" for the CURRENT layout: back to the admin-curated
 *  default (or clean layout look) and the layout's own stash is wiped —
 *  as if it were being picked for the first time again. */
export function resetLayoutToDefault(
   value: PortalConfig,
   curated?: Partial<PortalConfig>
): PortalConfig {
   const d = defaultPortalConfig();
   const layout = value.layout || 'classic';
   const stash = { ...(value.layoutConfigs || {}) };
   delete stash[layout];
   const base: PortalConfig = {
      ...value,
      accent: '',
      background: {
         ...d.background,
         color: LAYOUT_DEFAULT_BG[layout] ?? d.background.color,
      },
      audio: { ...d.audio },
      effects: [],
      slider: { ...d.slider },
      layoutConfigs: stash,
   };
   if (!curated) return base;
   return {
      ...base,
      accent: curated.accent ?? '',
      background: { ...base.background, ...(curated.background || {}) },
      audio: { ...base.audio, ...(curated.audio || {}) },
      effects: Array.isArray(curated.effects) ? [...curated.effects] : [],
      slider: { ...base.slider, ...(curated.slider || {}) },
      buttons: curated.buttons
         ? {
              insertCoin: {
                 ...(value.buttons?.insertCoin || {}),
                 ...((curated.buttons as any).insertCoin || {}),
              },
              wifiRates: {
                 ...(value.buttons?.wifiRates || {}),
                 ...((curated.buttons as any).wifiRates || {}),
              },
              voucher: {
                 ...(value.buttons?.voucher || {}),
                 ...((curated.buttons as any).voucher || {}),
              },
           }
         : value.buttons,
   };
}

/* ── Pre-made background presets (Background tab) ────────────────────
   `css` paints the swatch AND the portal page (gradient presets land in
   background.gradient; solids just set the color). `base` is the flat
   color used for pageBg + light/dark text decisions. */
export interface BgPreset {
   name: string;
   base: string;
   /** CSS gradient string; '' = solid. */
   gradient: string;
}

export const BG_PRESETS: BgPreset[] = [
   // Solids — darks
   { name: 'Midnight', base: '#0e1420', gradient: '' },
   { name: 'Charcoal', base: '#17191e', gradient: '' },
   { name: 'Deep Purple', base: '#1a1030', gradient: '' },
   { name: 'Forest', base: '#0f1f18', gradient: '' },
   { name: 'Navy', base: '#101c36', gradient: '' },
   { name: 'Espresso', base: '#241a14', gradient: '' },
   { name: 'Wine', base: '#2b0f1a', gradient: '' },
   { name: 'Slate Blue', base: '#1d2533', gradient: '' },
   // Solids — lights
   { name: 'Pure White', base: '#ffffff', gradient: '' },
   { name: 'Cream', base: '#f6f1e7', gradient: '' },
   { name: 'Sky', base: '#eaf4ff', gradient: '' },
   { name: 'Blush', base: '#fdeef2', gradient: '' },
   { name: 'Lavender', base: '#f0ecfb', gradient: '' },
   { name: 'Sand', base: '#f3ead9', gradient: '' },
   { name: 'Mint Ice', base: '#e9f7f1', gradient: '' },
   { name: 'Pearl Gray', base: '#eff1f4', gradient: '' },
   // Gradients — dark & moody
   { name: 'Midnight City', base: '#203a43', gradient: 'linear-gradient(180deg, #0f2027 0%, #203a43 55%, #2c5364 100%)' },
   { name: 'Deep Space', base: '#002a5c', gradient: 'linear-gradient(180deg, #000428 0%, #004e92 100%)' },
   { name: 'Royal Purple', base: '#38184f', gradient: 'linear-gradient(180deg, #41295a 0%, #2f0743 100%)' },
   { name: 'Ember', base: '#4a1207', gradient: 'linear-gradient(180deg, #1f1c18 0%, #8e0e00 100%)' },
   { name: 'Neon Dusk', base: '#241b4d', gradient: 'linear-gradient(180deg, #0f0c29 0%, #302b63 55%, #24243e 100%)' },
   { name: 'Emerald Night', base: '#0d3b2e', gradient: 'linear-gradient(180deg, #061e17 0%, #10553f 100%)' },
   { name: 'Charcoal Fade', base: '#1c1f26', gradient: 'linear-gradient(180deg, #0c0e12 0%, #2a2f3a 100%)' },
   { name: 'Midnight Gold', base: '#2a2118', gradient: 'linear-gradient(180deg, #15100a 0%, #3d2f1c 60%, #57422a 100%)' },
   // Gradients — vivid
   { name: 'Sunset Glow', base: '#c2274e', gradient: 'linear-gradient(180deg, #ff512f 0%, #dd2476 100%)' },
   { name: 'Aurora', base: '#4a92a0', gradient: 'linear-gradient(180deg, #13547a 0%, #80d0c7 100%)' },
   { name: 'Tropic Punch', base: '#c85a32', gradient: 'linear-gradient(180deg, #f83600 0%, #f9d423 100%)' },
   { name: 'Grape Soda', base: '#5d3fd3', gradient: 'linear-gradient(180deg, #7f00ff 0%, #e100ff 100%)' },
   { name: 'Blue Lagoon', base: '#1a6fae', gradient: 'linear-gradient(180deg, #0052d4 0%, #4364f7 55%, #6fb1fc 100%)' },
   { name: 'Cherry Cola', base: '#7c1f3a', gradient: 'linear-gradient(180deg, #42060f 0%, #a4133c 100%)' },
   { name: 'Mango Tango', base: '#e0813c', gradient: 'linear-gradient(180deg, #ffe259 0%, #ffa751 100%)' },
   { name: 'Northern Lights', base: '#2c6e63', gradient: 'linear-gradient(160deg, #0f3443 0%, #34e89e 100%)' },
   // Gradients — soft & light
   { name: 'Ocean Breeze', base: '#bcdcf2', gradient: 'linear-gradient(180deg, #dceffc 0%, #8fc1e8 100%)' },
   { name: 'Champagne', base: '#efe0c4', gradient: 'linear-gradient(180deg, #f7efe1 0%, #e3cfa8 100%)' },
   { name: 'Fresh Mint', base: '#d2f0e2', gradient: 'linear-gradient(180deg, #e8f8f2 0%, #b7e9d3 100%)' },
   { name: 'Peach Cream', base: '#f8d9c8', gradient: 'linear-gradient(180deg, #fdf0e9 0%, #f6c8ab 100%)' },
   { name: 'Lilac Haze', base: '#ded4f2', gradient: 'linear-gradient(180deg, #f3eefc 0%, #c9b8ec 100%)' },
   { name: 'Rose Water', base: '#f5d4dd', gradient: 'linear-gradient(180deg, #fdeff3 0%, #f0bccb 100%)' },
   // Macarons — soft pastel sweets
   { name: 'Pistachio', base: '#cfe6c2', gradient: 'linear-gradient(180deg, #e9f5e0 0%, #b8d9a5 100%)' },
   { name: 'Vanilla Bean', base: '#f4ecd7', gradient: 'linear-gradient(180deg, #fbf7ec 0%, #ecdfc0 100%)' },
   { name: 'Strawberry Milk', base: '#f7cfd6', gradient: 'linear-gradient(180deg, #fce8ec 0%, #f2b6c1 100%)' },
   { name: 'Blueberry Cream', base: '#ccd6f0', gradient: 'linear-gradient(180deg, #e8edfa 0%, #b3c2e8 100%)' },
   { name: 'Lemon Chiffon', base: '#f7eec2', gradient: 'linear-gradient(180deg, #fdf8e0 0%, #f0e3a4 100%)' },
   { name: 'Taro Latte', base: '#ddc9e8', gradient: 'linear-gradient(180deg, #f1e8f7 0%, #c9abdd 100%)' },
];


/* ── Hotel layout accent palettes (Styles tab) ─────────────────────── */
export const HOTEL_ACCENTS: { key: string; name: string; hex: string }[] = [
   // Metallics
   { key: '', name: 'Champagne Gold', hex: '#c9a45c' },
   { key: '#d4af37', name: 'Royal Gold', hex: '#d4af37' },
   { key: '#b9c0cc', name: 'Platinum Silver', hex: '#b9c0cc' },
   { key: '#d8a29a', name: 'Rose Gold', hex: '#d8a29a' },
   { key: '#b6764a', name: 'Copper', hex: '#b6764a' },
   { key: '#8c8578', name: 'Titanium', hex: '#8c8578' },
   // Jewel tones
   { key: '#2f8f6b', name: 'Emerald', hex: '#2f8f6b' },
   { key: '#3a9188', name: 'Teal Lagoon', hex: '#3a9188' },
   { key: '#33549c', name: 'Sapphire', hex: '#33549c' },
   { key: '#8e3a48', name: 'Burgundy', hex: '#8e3a48' },
   { key: '#5d3f8e', name: 'Amethyst', hex: '#5d3f8e' },
   { key: '#b8384e', name: 'Ruby', hex: '#b8384e' },
   { key: '#22335c', name: 'Midnight Blue', hex: '#22335c' },
   { key: '#3d6b4f', name: 'Forest Green', hex: '#3d6b4f' },
   // Warm & fresh
   { key: '#e8735a', name: 'Sunset Coral', hex: '#e8735a' },
   { key: '#e8a87c', name: 'Peach', hex: '#e8a87c' },
   { key: '#4a90d9', name: 'Ocean Blue', hex: '#4a90d9' },
   { key: '#58b898', name: 'Mint', hex: '#58b898' },
   { key: '#9b8ec4', name: 'Lavender', hex: '#9b8ec4' },
   // Neutrals
   { key: '#6b7686', name: 'Slate', hex: '#6b7686' },
   { key: '#2b2e35', name: 'Onyx', hex: '#2b2e35' },
   { key: '#e9e2d0', name: 'Ivory', hex: '#e9e2d0' },
];


/* ── Beach layout CTA palettes (Styles tab) — summer vibes ─────────── */
export const BEACH_ACCENTS: { key: string; name: string; hex: string }[] = [
   { key: '', name: 'Coral', hex: '#ff7a59' },
   { key: '#f4783b', name: 'Sunset Orange', hex: '#f4783b' },
   { key: '#f5a524', name: 'Mango', hex: '#f5a524' },
   { key: '#f2c14e', name: 'Sunny Yellow', hex: '#f2c14e' },
   { key: '#ef5b74', name: 'Watermelon', hex: '#ef5b74' },
   { key: '#d9418c', name: 'Dragonfruit', hex: '#d9418c' },
   { key: '#2fae7d', name: 'Palm Green', hex: '#2fae7d' },
   { key: '#7ac74f', name: 'Lime Soda', hex: '#7ac74f' },
   { key: '#0e9bd8', name: 'Ocean Blue', hex: '#0e9bd8' },
   { key: '#16b8c4', name: 'Lagoon', hex: '#16b8c4' },
   { key: '#16697a', name: 'Deep Sea', hex: '#16697a' },
   { key: '#9b8ec4', name: 'Halo-halo Ube', hex: '#9b8ec4' },
];
